import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Meeting, MeetingNotesRef } from '../api/types.js';
import { useMeetingNotes, useMeetings, useStartMeetingSession } from '../hooks/use-meetings.js';
import { Button } from './button.js';
import { MeetingPlanBlock } from './meeting-plan.js';
import { PlainText } from './plain-text.js';
import { timeAgo } from '../utils/time-ago.js';
import { byDay, startOf } from '../utils/meeting-days.js';

// « Meetings »: the operator's meetings of the last 14 days and the next 7, from this server's calendar (ADR-037), laid
// out as a day's agenda: the time in a gutter, then the meeting. The notes Gemini writes are read from Google Docs when
// asked; they are other people's words, shown as plain text only.

const clock = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
const RESPONSE: Record<string, string> = { accepted: 'Accepted', tentative: 'Tentative', declined: 'Declined', needsAction: 'Not answered' };

function NotesItem({ serverId, eventId, n }: { serverId: string; eventId: string; n: MeetingNotesRef }) {
  const [open, setOpen] = useState(false);
  const { data, error, isFetching } = useMeetingNotes(serverId, eventId, n.docId, open);
  const id = `notes-${eventId}-${n.docId}`;
  const denied = data?.status === 'denied' || (!data && n.read?.status === 'denied');
  return (
    <li className="text-xs">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span style={{ color: 'var(--color-text-2)' }}>{n.title}</span>
        {denied ? (
          <span style={{ color: 'var(--color-warn)' }}>Not shared with you: Google refused to open it.</span>
        ) : (
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={id}
            className="font-medium hover:underline focus-visible:outline focus-visible:outline-2 max-lg:min-h-10" style={{ color: 'var(--color-brand-light)' }}>
            {open ? 'Hide the notes' : 'Read the notes'}
          </button>
        )}
        <a href={n.url} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: 'var(--color-brand-light)' }}>Open in Google Docs</a>
        {n.read?.status === 'ok' && <span style={{ color: 'var(--color-text-3)' }}>read {timeAgo(n.read.readAt)}</span>}
      </div>
      {open && !denied && (
        <div id={id} className="mt-2" aria-live="polite">
          {isFetching && !data ? <p style={{ color: 'var(--color-text-3)' }}>Reading the notes in Google Docs…</p> : null}
          {error ? <p role="alert" style={{ color: 'var(--color-error)' }}>Not read: {(error as Error).message}</p> : null}
          {data?.status === 'ok' && (
            <div className="max-h-[28rem] overflow-auto rounded-md border p-2" style={{ borderColor: 'var(--color-border-2)', backgroundColor: 'var(--color-surface-2)' }}>
              <PlainText text={data.text || '(empty)'} className="text-sm" />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Start a session (ADR-037 step 2): a main session of the server from the meeting and its notes; the same session when
 * started again. Admins only; everyone sees where the session is.
 */
function MeetingSession({ serverId, m, isAdmin }: { serverId: string; m: Meeting; isAdmin: boolean }) {
  const start = useStartMeetingSession(serverId);
  if (!isAdmin && !m.sessionId) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {isAdmin && (
        <Button size="sm" variant="ghost" className="max-lg:min-h-10" disabled={start.isPending} onClick={() => start.mutate({ eventId: m.eventId })}>
          {start.isPending ? 'Starting the session…' : m.sessionId ? 'Ask the session again' : 'Start a session'}
        </Button>
      )}
      {m.sessionId && (
        <Link to={`/servers/${serverId}/sessions/${m.sessionId}`} className="text-xs hover:underline" style={{ color: 'var(--color-brand-light)' }}>Open the session</Link>
      )}
      <span aria-live="polite" className="text-xs" style={{ color: 'var(--color-text-3)' }}>
        {start.isSuccess ? 'Session started: it reads the meeting and its notes.' : ''}
      </span>
      {start.error && <p role="alert" className="basis-full text-xs" style={{ color: 'var(--color-error)' }}>Not started: {(start.error as Error).message}</p>}
    </div>
  );
}

function MeetingRow({ serverId, m, isAdmin }: { serverId: string; m: Meeting; isAdmin: boolean }) {
  const start = startOf(m);
  const others = m.attendees.filter((a) => !a.self);
  const response = m.response ? RESPONSE[m.response] ?? m.response : undefined;
  return (
    <li id={`meeting-${m.eventId}`} className="grid grid-cols-[3.5rem_1fr] gap-x-3 border-t py-3 first:border-t-0" style={{ borderColor: 'var(--color-border)' }}>
      <div className="pt-px text-sm tabular-nums" style={{ color: 'var(--color-text-2)' }}>
        {m.allDay ? <span className="text-xs">All day</span> : (
          <>
            <div>{clock.format(start)}</div>
            <div className="text-xs" style={{ color: 'var(--color-text-3)' }}>{clock.format(new Date(m.end))}</div>
          </>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium" style={{ color: 'var(--color-text)' }}>
          {m.link ? <a href={m.link} target="_blank" rel="noreferrer" className="hover:underline">{m.summary}</a> : m.summary}
        </p>
        <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-3)' }}>
          {m.organizer ? `Organised by ${m.organizer}` : 'No organiser'}{response ? `. You: ${response}` : ''}
        </p>
        {others.length > 0 && (
          <details className="mt-1 text-xs" style={{ color: 'var(--color-text-2)' }}>
            <summary className="cursor-pointer max-lg:min-h-10">{others.length === 1 ? 'With 1 person' : `With ${others.length} people`}</summary>
            <ul className="mt-1 space-y-0.5">
              {others.map((a) => <li key={a.email} className="break-all">{a.name ? `${a.name} <${a.email}>` : a.email}{a.response ? `: ${RESPONSE[a.response] ?? a.response}` : ''}</li>)}
            </ul>
          </details>
        )}
        {m.notes.length > 0 && (
          <ul className="mt-2 space-y-2 border-l-2 pl-3" style={{ borderColor: 'var(--color-brand)' }} aria-label={`Notes of ${m.summary}`}>
            {m.notes.map((n) => <NotesItem key={n.docId} serverId={serverId} eventId={m.eventId} n={n} />)}
          </ul>
        )}
        <MeetingSession serverId={serverId} m={m} isAdmin={isAdmin} />
        {m.plan && <MeetingPlanBlock serverId={serverId} eventId={m.eventId} plan={m.plan} isAdmin={isAdmin} />}
      </div>
    </li>
  );
}

export function MeetingsTab({ serverId, isAdmin = false }: { serverId: string; isAdmin?: boolean }) {
  const { data, error, isLoading } = useMeetings(serverId);
  const muted = { color: 'var(--color-text-2)' };
  const settings = (label: string) => <Link to={`/servers/${serverId}/settings#notify-title`} className="underline" style={{ color: 'var(--color-brand-light)' }}>{label}</Link>;
  if (isLoading) return <p className="text-sm" style={{ color: 'var(--color-text-3)' }}>Reading the calendar…</p>;
  if (error) return <p role="alert" className="text-sm" style={{ color: 'var(--color-error)' }}>Could not read the meetings: {(error as Error).message}</p>;
  if (!data?.connected) return <p className="text-sm" style={muted}>Meetings come from the server&apos;s Google connection: {settings('connect Google')} first.</p>;
  const now = new Date();
  const groups = byDay(data.meetings, now);
  const withNotes = data.meetings.filter((m) => m.notes.length).length;
  return (
    <div>
      <p className="mb-4 text-xs" style={{ color: 'var(--color-text-3)' }}>
        {data.email}, the last 14 days and the next 7{withNotes ? `; ${withNotes === 1 ? 'one meeting has' : `${withNotes} meetings have`} notes` : ''}.
      </p>
      {data.needsReconnect && (
        <p className="mb-4 text-sm" style={muted}>Google was connected before aidev read meeting notes: {settings('reconnect Google')} and tick the Google Docs permission.</p>
      )}
      {groups.length === 0 && <p className="text-sm" style={muted}>No meeting with someone else in the last 14 days or the next 7.</p>}
      {groups.map((g) => (
        <section key={g.key} className="mb-5" aria-label={g.label}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{g.label}</h3>
          <ul className="mt-1">
            {g.meetings.map((m) => <MeetingRow key={m.eventId} serverId={serverId} m={m} isAdmin={isAdmin} />)}
          </ul>
        </section>
      ))}
    </div>
  );
}
